
import { TimerType, TimerState, TimerSettings } from './timerTypes';
import {
  handleResetTimer,
  handleToggleTimer,
  handleSetProgress,
  handleSetTimeLeft,
  handleSetRunning,
  handleIncrementPomodoros,
  handleSetFirstStart,
  handleSetSessionIndex,
  handleSetSequencePosition,
  handleUpdateSettings
} from './reducerHandlers';
import { getTotalTimeForType } from './timerUtils';

const defaultSettings: TimerSettings = {
  pomodoro: 25,
  shortBreak: 5,
  longBreak: 30, // Changed from 15 to 30
  sessions: 4,
  autoRepeat: true
};

export const initialTimerState: TimerState = {
  timerType: 'pomodoro',
  timeLeft: defaultSettings.pomodoro * 60,
  isRunning: false,
  completedPomodoros: 0,
  currentSessionIndex: 0,
  progress: 0,
  elapsedSeconds: 0,
  sequencePosition: 0,
  isFirstStart: true,
  settings: defaultSettings,
  minutes: defaultSettings.pomodoro,
  seconds: 0,
  timerTitle: 'Pomodoro'
};

export type TimerAction = 
  | { type: 'RESET_TIMER'; payload: { timerType: TimerType; startRunning?: boolean; sequencePos?: number } }
  | { type: 'START' }
  | { type: 'STOP' }
  | { type: 'TICK' }
  | { type: 'CHANGE_TYPE'; payload: TimerType }
  | { type: 'SET_PROGRESS'; payload: number }
  | { type: 'SET_TIME_LEFT'; payload: number }
  | { type: 'SET_RUNNING'; payload: boolean }
  | { type: 'INCREMENT_POMODOROS' }
  | { type: 'SET_FIRST_START'; payload: boolean }
  | { type: 'SET_SESSION_INDEX'; payload: number }
  | { type: 'SET_SEQUENCE_POSITION'; payload: number }
  | { type: 'SET_COMPLETED_POMODOROS'; payload: number }
  | { type: 'UPDATE_SETTINGS'; payload: TimerSettings };

export const timerReducer = (state: TimerState, action: TimerAction): TimerState => {
  switch (action.type) {
    case 'RESET_TIMER': {
      const { timerType, startRunning, sequencePos } = action.payload;
      const newState = handleResetTimer(state, timerType, startRunning, sequencePos, state.settings);
      return {
        ...newState,
        minutes: Math.floor(newState.timeLeft / 60),
        seconds: newState.timeLeft % 60,
        timerTitle: timerType === 'pomodoro' ? 'Pomodoro' : 
                   timerType === 'shortBreak' ? 'Short Break' : 'Long Break'
      };
    }
    
    case 'START': {
      return {
        ...state,
        isRunning: true,
        isFirstStart: false
      };
    }
    
    case 'STOP': {
      return {
        ...state,
        isRunning: false
      };
    }
    
    case 'TICK': {
      if (state.timeLeft <= 0) {
        return state; // Don't tick if time is already 0
      }
      
      const newTimeLeft = state.timeLeft - 1;
      const newElapsedSeconds = state.elapsedSeconds + 1;
      
      // Calculate progress: how much of the timer has elapsed
      const totalTime = getTotalTimeForType(state.timerType, state.settings);
      const progress = ((totalTime - newTimeLeft) / totalTime) * 100;
      
      return {
        ...state,
        timeLeft: newTimeLeft,
        elapsedSeconds: newElapsedSeconds,
        progress: progress,
        minutes: Math.floor(newTimeLeft / 60),
        seconds: newTimeLeft % 60
      };
    }
    
    case 'CHANGE_TYPE': {
      const timerType = action.payload;
      const timeLeft = getTotalTimeForType(timerType, state.settings);
      return {
        ...state,
        timerType,
        timeLeft,
        elapsedSeconds: 0,
        progress: 0,
        minutes: Math.floor(timeLeft / 60),
        seconds: timeLeft % 60,
        timerTitle: timerType === 'pomodoro' ? 'Pomodoro' : 
                   timerType === 'shortBreak' ? 'Short Break' : 'Long Break',
        isRunning: false
      };
    }
    
    case 'SET_PROGRESS':
      return handleSetProgress(state, action.payload);
      
    case 'SET_TIME_LEFT': {
      const timeLeft = action.payload === -1 ? Math.max(0, state.timeLeft - 1) : action.payload;
      const newState = handleSetTimeLeft(state, timeLeft);
      return {
        ...newState,
        minutes: Math.floor(timeLeft / 60),
        seconds: timeLeft % 60
      };
    }
    
    case 'SET_RUNNING':
      return handleSetRunning(state, action.payload);
    
    case 'INCREMENT_POMODOROS':
      return handleIncrementPomodoros(state);
    
    case 'SET_FIRST_START':
      return handleSetFirstStart(state, action.payload);
    
    case 'SET_SESSION_INDEX':
      return handleSetSessionIndex(state, action.payload);
    
    case 'SET_SEQUENCE_POSITION':
      return handleSetSequencePosition(state, action.payload);
    
    case 'SET_COMPLETED_POMODOROS':
      return {
        ...state,
        completedPomodoros: action.payload
      };
    
    case 'UPDATE_SETTINGS': {
      const newState = handleUpdateSettings(state, action.payload);
      return {
        ...newState,
        settings: action.payload,
        minutes: Math.floor(newState.timeLeft / 60),
        seconds: newState.timeLeft % 60
      };
    }
    
    default:
      return state;
  }
};
